/**
 * Barra de busca, ordem, vista e filtros da lista de empresas.
 *
 * Decisoes nao obvias:
 *  1. Cinco grupos de filtro (status, provedor, funil, cobranca, saude), cada
 *     um de selecao unica (FilterChips), combinados entre si com E. O numero
 *     de cada opcao e FACETADO: quantas sobrariam ao escolhe-la, mantidos os
 *     outros filtros e a busca (`contar` em lista.ts).
 *  2. Opcao com zero resultados continua visivel (apagada pelo numero 0): some
 *     com ela e a pessoa acha que o filtro nao existe.
 *  3. Atalho "/" foca a busca de qualquer lugar da pagina (fora de campos de
 *     texto) -- o gesto padrao de quem opera lista grande.
 *  4. Em tela estreita os grupos ficam atras de "Filtros (N)": cinco linhas de
 *     chips empurrariam a lista para fora da primeira dobra no celular.
 *  5. Os filtros ativos viram pastilhas removiveis logo acima do resultado,
 *     com "Limpar tudo": o recorte atual fica sempre legivel em uma linha.
 */

import { useEffect, useId, useState } from 'react';
import { Button, FilterChips, Icon, Input, Panel, Select, type ChipOption } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import { contar, filtrosAtivos, type LinhaEmpresa } from '../lista';
import type { ConsultaLista, FiltroCobranca, FiltroFunil, FiltroProvedor, FiltroSaude, FiltroStatus, VistaLista } from '../types';
import { FUNIL_INFO, MODELO_COBRANCA, NIVEL_SAUDE, ORDENS_DISPONIVEIS, PROVEDORES, PROVEDOR_INFO, STATUS_EMPRESA } from '../vocabulario';

function ehCampoDeTexto(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) {
    return false;
  }
  const tag = alvo.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || alvo.isContentEditable;
}

export function FiltrosEmpresas({
  consulta,
  onConsulta,
  linhas,
  exibidas,
  vista,
}: {
  readonly consulta: ConsultaLista;
  readonly onConsulta: (patch: Partial<ConsultaLista>) => void;
  /** Todas as linhas (sem filtro), para as contagens facetadas. */
  readonly linhas: readonly LinhaEmpresa[];
  /** Quantas passaram no recorte atual. */
  readonly exibidas: number;
  readonly vista: VistaLista;
}): JSX.Element {
  const idBusca = useId();
  const idOrdem = useId();
  const idGrupos = useId();
  const [abertos, setAbertos] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || ehCampoDeTexto(event.target)) {
        return;
      }
      event.preventDefault();
      const campo = document.getElementById(idBusca);
      if (campo instanceof HTMLInputElement) {
        campo.focus();
        campo.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [idBusca]);

  const n = (patch: Partial<ConsultaLista>): string => formatCount(contar(linhas, consulta, patch));

  const status: readonly ChipOption<FiltroStatus>[] = [
    { value: 'todas', label: 'Todas', count: n({ status: 'todas' }) },
    { value: 'ativa', label: STATUS_EMPRESA.ativa.plural, count: n({ status: 'ativa' }) },
    { value: 'onboarding', label: STATUS_EMPRESA.onboarding.plural, count: n({ status: 'onboarding' }) },
    { value: 'pausada', label: STATUS_EMPRESA.pausada.plural, count: n({ status: 'pausada' }) },
  ];
  const provedor: readonly ChipOption<FiltroProvedor>[] = [
    { value: 'todos', label: 'Todos', count: n({ provedor: 'todos' }) },
    ...PROVEDORES.map((p) => ({ value: p, label: PROVEDOR_INFO[p].curto, count: n({ provedor: p }) })),
    { value: 'nenhum', label: 'Nenhum', count: n({ provedor: 'nenhum' }) },
  ];
  const funil: readonly ChipOption<FiltroFunil>[] = [
    { value: 'todos', label: 'Todos', count: n({ funil: 'todos' }) },
    { value: 'carrinho_abandonado', label: FUNIL_INFO.carrinho_abandonado.label, count: n({ funil: 'carrinho_abandonado' }) },
    { value: 'expirados', label: FUNIL_INFO.expirados.label, count: n({ funil: 'expirados' }) },
  ];
  const cobranca: readonly ChipOption<FiltroCobranca>[] = [
    { value: 'todos', label: 'Todos', count: n({ cobranca: 'todos' }) },
    { value: 'contrato', label: MODELO_COBRANCA.contrato.curto, count: n({ cobranca: 'contrato' }) },
    { value: 'automatico', label: MODELO_COBRANCA.automatico.curto, count: n({ cobranca: 'automatico' }) },
    { value: 'a_definir', label: MODELO_COBRANCA.a_definir.curto, count: n({ cobranca: 'a_definir' }) },
  ];
  const saude: readonly ChipOption<FiltroSaude>[] = [
    { value: 'todas', label: 'Todas', count: n({ saude: 'todas' }) },
    { value: 'critica', label: NIVEL_SAUDE.critica.label, count: n({ saude: 'critica' }) },
    { value: 'atencao', label: NIVEL_SAUDE.atencao.label, count: n({ saude: 'atencao' }) },
    { value: 'implantacao', label: NIVEL_SAUDE.implantacao.label, count: n({ saude: 'implantacao' }) },
    { value: 'saudavel', label: NIVEL_SAUDE.saudavel.label, count: n({ saude: 'saudavel' }) },
    { value: 'pausada', label: NIVEL_SAUDE.pausada.label, count: n({ saude: 'pausada' }) },
  ];
  const vistas: readonly ChipOption<VistaLista>[] = [
    { value: 'tabela', label: 'Tabela' },
    { value: 'cartoes', label: 'Cartões' },
  ];

  const ativos = filtrosAtivos(consulta);
  const qtdGrupos = ativos.filter((f) => f.chave !== 'busca').length;
  const ordemAtual = ORDENS_DISPONIVEIS.find((o) => o.coluna === consulta.ordem.coluna && o.desc === consulta.ordem.desc)?.valor ?? '';

  const limparTudo = (): void =>
    onConsulta({ busca: '', status: 'todas', provedor: 'todos', funil: 'todos', cobranca: 'todos', saude: 'todas' });

  return (
    <Panel className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <label htmlFor={idBusca} className="text-sm font-medium text-ink">
            Buscar empresa
          </label>
          <div className="relative">
            <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
            <Input
              id={idBusca}
              type="search"
              value={consulta.busca}
              onChange={(e) => onConsulta({ busca: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && consulta.busca !== '') {
                  e.preventDefault();
                  onConsulta({ busca: '' });
                }
              }}
              placeholder="Nome, segmento, contato, provedor ou saúde"
              autoComplete="off"
              aria-describedby={`${idBusca}-dica`}
              className="pl-9 pr-10"
            />
            <kbd aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-line bg-elevated px-1.5 text-[11px] text-ink-subtle sm:block">
              /
            </kbd>
          </div>
          <p id={`${idBusca}-dica`} className="text-xs text-ink-subtle">
            Tecle / para buscar de qualquer lugar; Esc limpa. Várias palavras se somam: “fit kiwify”.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 lg:w-64">
          <label htmlFor={idOrdem} className="text-sm font-medium text-ink">
            Ordenar por
          </label>
          <Select
            id={idOrdem}
            value={ordemAtual}
            onChange={(e) => {
              const escolhida = ORDENS_DISPONIVEIS.find((o) => o.valor === e.target.value);
              if (escolhida !== undefined) {
                onConsulta({ ordem: { coluna: escolhida.coluna, desc: escolhida.desc } });
              }
            }}
          >
            {ordemAtual === '' ? <option value="">Pela coluna escolhida na tabela</option> : null}
            {ORDENS_DISPONIVEIS.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">Exibir como</span>
          <div className="flex h-11 items-center">
            <FilterChips label="Exibir empresas como" options={vistas} value={vista} onChange={(v) => onConsulta({ vista: v })} tone="accent" />
          </div>
        </div>
      </div>

      <div className="md:hidden">
        <Button variant="secondary" size="sm" icon="filtro" aria-expanded={abertos} aria-controls={idGrupos} onClick={() => setAbertos((v) => !v)}>
          Filtros{qtdGrupos > 0 ? ` (${formatCount(qtdGrupos)} ativos)` : ''}
        </Button>
      </div>

      <div id={idGrupos} className={`${abertos ? 'flex' : 'hidden'} flex-col gap-3 border-t border-line pt-4 md:flex`}>
        <div className="grid gap-3 xl:grid-cols-2">
          <FilterChips label="Filtrar por status" prefix="Status:" options={status} value={consulta.status} onChange={(v) => onConsulta({ status: v })} />
          <FilterChips label="Filtrar por saúde" prefix="Saúde:" options={saude} value={consulta.saude} onChange={(v) => onConsulta({ saude: v })} />
          <FilterChips label="Filtrar por provedor de checkout" prefix="Provedor:" options={provedor} value={consulta.provedor} onChange={(v) => onConsulta({ provedor: v })} />
          <FilterChips label="Filtrar por funil ativo" prefix="Funil:" options={funil} value={consulta.funil} onChange={(v) => onConsulta({ funil: v })} />
          <FilterChips label="Filtrar por modelo de cobrança" prefix="Cobrança:" options={cobranca} value={consulta.cobranca} onChange={(v) => onConsulta({ cobranca: v })} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <p role="status" aria-live="polite" className="mr-1 text-sm text-ink-muted">
          <strong className="tabular-nums text-ink">{formatCount(exibidas)}</strong> de {formatCount(linhas.length)} {linhas.length === 1 ? 'empresa' : 'empresas'}
          {ativos.length > 0 ? ' no recorte' : ''}
        </p>
        {ativos.map((f) => (
          <button
            key={f.chave}
            type="button"
            onClick={() => onConsulta(f.limpar)}
            className="inline-flex h-7 items-center gap-1.5 rounded-full border border-brand/40 bg-brand/5 px-2.5 text-xs font-medium text-ink transition-colors hover:border-brand hover:bg-brand/10"
            aria-label={`Remover filtro ${f.rotulo}`}
          >
            {f.rotulo}
            <Icon name="x" size="xs" className="text-brand" />
          </button>
        ))}
        {ativos.length > 1 ? (
          <Button variant="ghost" size="sm" icon="x" onClick={limparTudo}>
            Limpar tudo
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}
